import { useMemo } from 'react';

import type { RecentSearch } from 'src/sections/search/types/recent-search';

import { extractRepoSlugsFromRecentSearches } from './extract-repo-slugs-from-recent-searches';
import { useRecentlyUpdatedRepositories } from './use-recently-updated-repositories';

export const REPO_FILTER_OPTIONS_DISPLAY_LIMIT = 5;

export interface UseRepoFilterOptionsResult {
  options: string[];
  isLoading: boolean;
}

/**
 * Returns repo filter options for the search dialog's `repo:` submenu.
 *
 * Prioritises repo slugs extracted from recent searches, then fills any
 * remaining slots (up to 5 total) with the most recently updated repos
 * from the API, skipping duplicates.
 */
export function useRepoFilterOptions(
  workspaceSlug: string,
  isRepoFilterActive: boolean,
  recentSearches: RecentSearch[]
): UseRepoFilterOptionsResult {
  const repoSlugsFromRecents = useMemo(
    () => extractRepoSlugsFromRecentSearches(recentSearches),
    [recentSearches]
  );

  const needsBackfill =
    repoSlugsFromRecents.length < REPO_FILTER_OPTIONS_DISPLAY_LIMIT;

  // Fetch recently updated repos to backfill any remaining slots.
  const { repositories: recentlyUpdatedRepositories, isLoading } =
    useRecentlyUpdatedRepositories(
      workspaceSlug,
      isRepoFilterActive && needsBackfill,
      '',
      REPO_FILTER_OPTIONS_DISPLAY_LIMIT
    );

  const options = useMemo(() => {
    const slugs = new Set(repoSlugsFromRecents);
    recentlyUpdatedRepositories.forEach(repo => slugs.add(repo.slug));
    return [...slugs].slice(0, REPO_FILTER_OPTIONS_DISPLAY_LIMIT);
  }, [repoSlugsFromRecents, recentlyUpdatedRepositories]);

  // fb-query initializes with isPending=true and never transitions out of it
  // when the query is disabled, so we guard with needsBackfill to avoid a
  // false loading state when recent searches already fill all slots.
  return { options, isLoading: needsBackfill && isLoading };
}
